import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faShoppingBag,
  faExchangeAlt,
  faSearch,
  faRocket,
  faSatellite,
  faMeteor
} from '@fortawesome/free-solid-svg-icons';
import '../assets/css/notfound.css';

const NotFound = () => {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const quickLinks = [
    { icon: faHome, label: 'Go Home', path: '/homepage', color: '#22C55E' },
    { icon: faShoppingBag, label: 'Shop Products', path: '/shop', color: '#3B82F6' },
    { icon: faExchangeAlt, label: 'Swap Deals', path: '/swap-deals', color: '#F59E0B' },
    { icon: faSearch, label: 'Search', path: '/shop', color: '#8B5CF6' }
  ];

  const floatingElements = [
    { icon: faRocket, delay: 0, duration: 20 },
    { icon: faSatellite, delay: 2, duration: 25 },
    { icon: faMeteor, delay: 4, duration: 18 }
  ];

  return (
    <div className="notfound-container">
      {/* Animated Background */}
      <div className="notfound-bg">
        <div className="notfound-grid"></div>
        <div
          className="notfound-glow"
          style={{
            left: mousePosition.x,
            top: mousePosition.y
          }}
        ></div>
      </div>

      {/* Floating Space Elements */}
      <div className="notfound-floating-elements">
        {floatingElements.map((element, index) => (
          <motion.div
            key={index}
            className="notfound-floating-icon"
            initial={{ opacity: 0, y: 100 }}
            animate={{
              opacity: [0.2, 0.5, 0.2],
              y: [-100, window.innerHeight + 100],
              x: [0, Math.random() * 100 - 50, 0]
            }}
            transition={{
              duration: element.duration,
              delay: element.delay,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            <FontAwesomeIcon icon={element.icon} />
          </motion.div>
        ))}
      </div>

      {/* Main Content */}
      <div className="notfound-content">
        {/* Glitch 404 Text */}
        <motion.div
          className="notfound-404"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="notfound-404-text" data-text="404">
            404
          </div>
          <div className="notfound-404-shadow">404</div>
        </motion.div>

        {/* Title and Description */}
        <motion.div
          className="notfound-text"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <h1 className="notfound-title">Lost in Space</h1>
          <p className="notfound-description">
            Oops! The page you're looking for has drifted into the void.
            Let's navigate you back to safety.
          </p>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          className="notfound-links"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          {quickLinks.map((link, index) => (
            <motion.button
              key={index}
              className="notfound-link-btn"
              onClick={() => navigate(link.path)}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 + index * 0.1 }}
              style={{ '--link-color': link.color }}
            >
              <div className="notfound-link-icon">
                <FontAwesomeIcon icon={link.icon} />
              </div>
              <span className="notfound-link-label">{link.label}</span>
              <div className="notfound-link-glow"></div>
            </motion.button>
          ))}
        </motion.div>

        {/* Auto Redirect Counter */}
        <motion.div
          className="notfound-redirect"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <div className="notfound-redirect-text">
            Or wait and we'll redirect you automatically
          </div>
          <div className="notfound-redirect-loader">
            <motion.div
              className="notfound-redirect-progress"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 5, ease: "linear" }}
              onAnimationComplete={() => navigate('/homepage')}
            ></motion.div>
          </div>
        </motion.div>

        {/* Animated Particles */}
        <div className="notfound-particles">
          {[...Array(20)].map((_, index) => (
            <motion.div
              key={index}
              className="notfound-particle"
              initial={{
                opacity: 0,
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight
              }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                delay: Math.random() * 2,
                repeat: Infinity,
                repeatDelay: Math.random() * 2
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NotFound;
